/**
 * The merchant-facing grouping of line states (SAI-1353).
 *
 * The state machine's fourteen states are the warehouse's vocabulary. A merchant scanning
 * their Returns tab wants nine plain words: is this waiting on me, is it on its way, is it
 * done. This file is the ONE place those two vocabularies meet, so the API filter, the row
 * label and the attention ordering can never disagree about which bucket a state is in.
 *
 * Display and filtering only. Nothing may branch on a group: branch on line state.
 */
import { z } from "zod";

import { LineStateSchema, type LineState } from "./return";

export const MerchantStateGroupSchema = z.enum([
  "waiting_for_you",
  "waiting_for_pickup",
  "with_courier",
  "at_warehouse",
  "inspected",
  "refund_due",
  "closed",
  "rejected",
  "cancelled",
]);
export type MerchantStateGroup = z.infer<typeof MerchantStateGroupSchema>;
export const MERCHANT_STATE_GROUPS = MerchantStateGroupSchema.options;

/**
 * Total over `LineState`: a state added to the machine without a row here is a type
 * error, not a row that silently falls out of the merchant's list.
 *
 * Two placements are judgement calls worth stating:
 *  - `booking_failed` sits under "waiting for pickup". The retry is the system's job, and
 *    from the merchant's chair the parcel is still at the shopper's door either way.
 *  - `restocked` is "refund due": the goods are back on the shelf and sellable, so the
 *    only thing left is the merchant honouring the resolution the shopper asked for.
 *    This app never moves money (docs/11), which is exactly why the merchant needs to be
 *    told it is their turn.
 *  - `quarantined`, `disposed`, `lost_in_transit` and `expired` are all "closed": nothing
 *    more happens to the return inside this system. Whether a claim or a rework bill
 *    follows is a Sirdab conversation, not a row state.
 */
const STATE_TO_GROUP: Readonly<Record<LineState, MerchantStateGroup>> = {
  requested: "waiting_for_you",
  approved: "waiting_for_pickup",
  booking_failed: "waiting_for_pickup",
  awaiting_pickup: "waiting_for_pickup",
  in_transit: "with_courier",
  received: "at_warehouse",
  inspected: "inspected",
  restocked: "refund_due",
  quarantined: "closed",
  disposed: "closed",
  lost_in_transit: "closed",
  expired: "closed",
  rejected: "rejected",
  cancelled: "cancelled",
  completed: "closed",
};

export function stateGroupOf(state: LineState): MerchantStateGroup {
  return STATE_TO_GROUP[state];
}

/** Every line state that renders under `group`. Never empty: each group owns at least one. */
export function statesInGroup(group: MerchantStateGroup): readonly LineState[] {
  return LineStateSchema.options.filter(
    (state) => STATE_TO_GROUP[state] === group,
  );
}

/**
 * What needs the merchant first. Lower sorts higher.
 *
 *  0. Waiting on a decision only they can make.
 *  1. Goods are back and the shopper is owed their refund, exchange or credit.
 *  2. In flight: nothing to do yet, but worth a glance.
 *  3. Over. Rejected, cancelled or otherwise closed.
 *
 * Within one rank the newest return comes first, so this is a bucket order, not a total
 * order over groups: every in-flight group shares rank 2 on purpose.
 */
const GROUP_ATTENTION_RANK: Readonly<Record<MerchantStateGroup, number>> = {
  waiting_for_you: 0,
  refund_due: 1,
  waiting_for_pickup: 2,
  with_courier: 2,
  at_warehouse: 2,
  inspected: 2,
  closed: 3,
  rejected: 3,
  cancelled: 3,
};

export function attentionRankOf(group: MerchantStateGroup): number {
  return GROUP_ATTENTION_RANK[group];
}

/**
 * How a merchant list is ordered. `newest` is the plain keyset order every integration
 * has paged with so far; `attention` is what the Returns tab shows by default.
 */
export const ListReturnsSortSchema = z.enum(["attention", "newest"]);
export type ListReturnsSort = z.infer<typeof ListReturnsSortSchema>;
